import { Link } from "react-router-dom";
import LegalLayout, { Section } from "@/components/LegalLayout";
import { COMPANY } from "@/lib/legal";

const TOC = [
  { id: "ikhtisar", label: "1. Ikhtisar" },
  { id: "sifat", label: "2. Sifat Layanan Digital" },
  { id: "langganan", label: "3. Pengembalian Langganan" },
  { id: "kredit", label: "4. Pengembalian Paket Kredit" },
  { id: "cara", label: "5. Cara Mengajukan Refund" },
  { id: "proses", label: "6. Proses & Waktu" },
  { id: "metode", label: "7. Metode Pengembalian" },
  { id: "tidak", label: "8. Tidak Dapat Dikembalikan" },
  { id: "chargeback", label: "9. Sengketa & Chargeback" },
  { id: "kontak", label: "10. Kontak" },
];

const link = "text-sky-600 font-medium hover:underline";
const ul = "list-disc pl-5 space-y-1.5";

export default function RefundPolicy() {
  return (
    <LegalLayout
      seoTitle={`Kebijakan Pengembalian Dana — ${COMPANY.brand}`}
      seoDesc={`Kebijakan Pengembalian Dana (Refund) ${COMPANY.brand} — syarat, prosedur, dan jangka waktu pengembalian untuk langganan dan paket kredit.`}
      path="/refund"
      title="Kebijakan Pengembalian Dana"
      subtitle={`Kami ingin Anda puas menggunakan ${COMPANY.brand}. Halaman ini menjelaskan kapan dan bagaimana pengembalian dana (refund) dapat diajukan.`}
      toc={TOC}
    >
      <Section id="ikhtisar" title="1. Ikhtisar">
        <p>
          Kebijakan ini berlaku untuk semua pembelian di {COMPANY.brand} ({COMPANY.website}), termasuk
          langganan paket dan paket kredit (PAYG), yang dibayarkan melalui mitra pembayaran resmi kami
          (<b>Midtrans</b> dan/atau <b>iPaymu</b>). Dengan melakukan pembelian, Anda menyetujui ketentuan ini
          bersama <Link to="/terms" className={link}>Syarat &amp; Ketentuan</Link>.
        </p>
      </Section>

      <Section id="sifat" title="2. Sifat Layanan Digital">
        <p>
          {COMPANY.brand} adalah layanan digital yang sumber dayanya (kredit, kuota, pengiriman pesan/email,
          dan pengambilan data) dikonsumsi secara langsung. Karena itu, pengembalian dana dipertimbangkan
          berdasarkan tingkat pemakaian dan ketentuan di bawah ini.
        </p>
      </Section>

      <Section id="langganan" title="3. Pengembalian untuk Langganan">
        <ul className={ul}>
          <li><b>Garansi 7 hari (pelanggan baru):</b> Pembelian langganan pertama kali dapat diajukan refund penuh dalam <b>7 (tujuh) hari kalender</b> sejak pembayaran, <b>selama pemakaian masih minimal</b> (mis. kredit/kuota terpakai &lt; 10% dari paket).</li>
          <li><b>Kesalahan teknis dari pihak kami:</b> Jika layanan inti tidak dapat digunakan akibat kesalahan kami dan tidak dapat kami perbaiki dalam waktu wajar, Anda berhak atas refund pro-rata atau penuh.</li>
          <li><b>Pembayaran ganda / salah nominal:</b> Kelebihan atau duplikasi pembayaran akan dikembalikan penuh setelah diverifikasi.</li>
          <li><b>Perpanjangan otomatis:</b> Tagihan perpanjangan umumnya tidak dapat dikembalikan setelah periode baru berjalan dan dipakai; mohon batalkan perpanjangan sebelum tanggal tagih.</li>
        </ul>
      </Section>

      <Section id="kredit" title="4. Pengembalian untuk Paket Kredit (PAYG)">
        <ul className={ul}>
          <li><b>Kredit yang belum terpakai</b> dapat diajukan refund dalam <b>7 hari</b> sejak pembelian.</li>
          <li><b>Kredit yang sudah terpakai tidak dapat dikembalikan</b>, karena sumber daya telah dikonsumsi.</li>
          <li>Jika hanya sebagian terpakai, refund dapat diberikan secara <b>pro-rata</b> atas sisa kredit yang belum digunakan, atas kebijakan kami.</li>
        </ul>
      </Section>

      <Section id="cara" title="5. Cara Mengajukan Pengembalian Dana">
        <p>Kirim permohonan ke <a href={`mailto:${COMPANY.email}`} className={link}>{COMPANY.email}</a> atau WhatsApp <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className={link}>{COMPANY.whatsappDisplay}</a> dengan menyertakan:</p>
        <ul className={ul}>
          <li>Nama & email akun terdaftar.</li>
          <li>Nomor Order/Invoice (mis. SUB-xxxx / CRD-xxxx).</li>
          <li>Tanggal & nominal transaksi.</li>
          <li>Alasan permohonan refund.</li>
        </ul>
      </Section>

      <Section id="proses" title="6. Proses & Waktu Pengembalian">
        <ul className={ul}>
          <li>Kami meninjau permohonan dalam <b>2–3 hari kerja</b>.</li>
          <li>Bila disetujui, dana dikembalikan dalam <b>7–14 hari kerja</b>, tergantung bank/penyedia pembayaran.</li>
          <li>Kami dapat meminta informasi tambahan untuk verifikasi guna mencegah penyalahgunaan.</li>
        </ul>
      </Section>

      <Section id="metode" title="7. Metode Pengembalian">
        <p>
          Pengembalian dilakukan melalui kanal/metode pembayaran yang sama dengan transaksi awal (melalui
          Midtrans/iPaymu) bila memungkinkan. Jika tidak memungkinkan, kami dapat mengembalikan via transfer
          bank ke rekening atas nama pemilik akun. Biaya administrasi penyedia pembayaran (bila ada) dapat
          dipotong sesuai ketentuan yang berlaku.
        </p>
      </Section>

      <Section id="tidak" title="8. Hal yang Tidak Dapat Dikembalikan">
        <ul className={ul}>
          <li>Kredit/kuota yang sudah terpakai atau pesan/email yang sudah terkirim.</li>
          <li>Permohonan setelah lewat jangka waktu yang ditentukan (mis. &gt; 7 hari) tanpa alasan teknis dari kami.</li>
          <li>Akun yang dihentikan karena pelanggaran <Link to="/terms" className={link}>Syarat &amp; Ketentuan</Link> (mis. spam, penipuan, penyalahgunaan).</li>
          <li>Masalah yang berada di luar kendali kami (mis. pemblokiran oleh penyedia pihak ketiga akibat aktivitas Anda).</li>
          <li>Sekadar berubah pikiran setelah layanan dipakai secara signifikan.</li>
        </ul>
      </Section>

      <Section id="chargeback" title="9. Sengketa & Chargeback">
        <p>
          Sebelum mengajukan sengketa/chargeback ke bank, mohon hubungi kami terlebih dahulu — sebagian besar
          masalah dapat kami selesaikan dengan cepat. Chargeback yang diajukan tanpa dasar dapat menyebabkan
          penangguhan akun selama proses verifikasi.
        </p>
      </Section>

      <Section id="kontak" title="10. Kontak">
        <p>
          Tim dukungan {COMPANY.brand}: <a href={`mailto:${COMPANY.email}`} className={link}>{COMPANY.email}</a> ·{" "}
          <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className={link}>WhatsApp {COMPANY.whatsappDisplay}</a>.
          Lihat juga <Link to="/faq" className={link}>FAQ</Link> untuk pertanyaan umum.
        </p>
      </Section>
    </LegalLayout>
  );
}
